import React from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
import Button, { LoadingButton } from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line import/order
import Tooltip from '@atlaskit/tooltip';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import {
	REQUEST_GROUP_DUPLICATE_NAME_ERROR,
	REQUEST_GROUP_NAME_REQUIRED_ERROR,
	REQUEST_GROUP_NAME_TOO_LONG_ERROR,
	REQUEST_TYPE_INVALID_ERROR,
	REQUEST_TYPE_NOT_FOUND_ERROR,
} from '../../../common/constants';
import messages from './messages';

const ERROR_KEY_MESSAGE: {
	[key: string]: MessageDescriptor;
} = {
	[REQUEST_GROUP_DUPLICATE_NAME_ERROR]: messages.duplicateNameError,
	[REQUEST_TYPE_INVALID_ERROR]: messages.invalidRequestFormError,
	[REQUEST_TYPE_NOT_FOUND_ERROR]: messages.missingRequestFormError,
	[REQUEST_GROUP_NAME_REQUIRED_ERROR]: messages.missingGroupNameError,
	[REQUEST_GROUP_NAME_TOO_LONG_ERROR]: messages.groupNameTooLongError,
};

const getErrorMessage = (error: ErrorResponse): MessageDescriptor => {
	const reasonKey = error.response && error.response.reasonKey;
	return ERROR_KEY_MESSAGE[reasonKey] || messages.genericError;
};

export type Props = {
	isSaving: boolean;
	canSave: boolean;
	error: ErrorResponse | undefined;
	onCreate: () => void;
	onCancel: () => void;
};

const CreateToolbar = ({ isSaving, canSave, error, onCancel, onCreate }: Props) => {
	const { formatMessage } = useIntl();

	return (
		<Wrapper>
			{error && (
				<>
					<ErrorIcon label="" primaryColor={token('color.icon.danger', colors.R400)} />
					<ErrorMessage>{formatMessage(getErrorMessage(error))}</ErrorMessage>
				</>
			)}
			<Toolbar>
				<Button
					appearance="subtle-link"
					onClick={(_, analyticEvent) => {
						fireUIAnalytics(analyticEvent, 'groupTemplateCancel');
						onCancel();
					}}
					isDisabled={isSaving}
				>
					{formatMessage(messages.cancelButton)}
				</Button>
				<Gap />
				<Tooltip content={canSave ? '' : formatMessage(messages.gateMessage)}>
					<LoadingButton
						testId="servicedesk-portal-settings-request-groups.ui.portal-group.create-toolbar.save-button"
						appearance="primary"
						onClick={(_, analyticEvent) => {
							fireUIAnalytics(analyticEvent, 'groupTemplateSave');
							onCreate();
						}}
						isDisabled={!canSave}
						isLoading={isSaving}
					>
						{formatMessage(messages.saveButton)}
					</LoadingButton>
				</Tooltip>
			</Toolbar>
		</Wrapper>
	);
};

CreateToolbar.defaultProps = {
	isSaving: false,
	canSave: false,
	error: undefined,
	onCreate: noop,
	onCancel: noop,
};

export default CreateToolbar;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	display: 'flex',
	alignItems: 'center',
	marginTop: token('space.100', '8px'),
	marginLeft: token('space.300', '24px'),
	marginRight: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ErrorMessage = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.danger', colors.R400),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	lineHeight: `${gridSize * 3}px`,
	marginLeft: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Toolbar = styled.div({
	display: 'flex',
	flexGrow: 1,
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Gap = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize}px`,
});
